import { relations } from "drizzle-orm/relations"
// Import tables directly from their respective schema files to avoid circular dependency
import {
  projectsTable,
  merchandiseGroup,
  merchandiseGroupConfiguration,
  category,
  countries,
  stores,
  storeConfigurations,
  storeMetadataExtensions,
  projectPresence,
  projectLabelAssignments,
  projectLabels,
} from "./app-schema"
import { organization, invitation, user, member, session, account } from "./auth-schema"
import {
  changeProjectRequests,
  changeProjectRunItems,
  changeProjectRuns,
  changeProjectTargets,
  changeProjects,
} from "./change-project-schema"
import { evaluationBatches, evaluationBenchmarks, evaluationRuns } from "./evaluation-schema"
import {
  featureFlags,
  flagEnvironments,
  flagUserOverrides,
  flagChangeLog,
} from "./feature-flags-schema"
import { configurationVersions } from "./store-configuration-schema"

export const projectRelations = relations(projectsTable, ({ one, many }) => ({
  store: one(stores, {
    fields: [projectsTable.storeId],
    references: [stores.id],
  }),
  configurationVersion: one(configurationVersions, {
    fields: [projectsTable.configVersionId],
    references: [configurationVersions.id],
  }),
  approver: one(user, {
    fields: [projectsTable.approvedBy],
    references: [user.id],
  }),
  labelAssignments: many(projectLabelAssignments),
}))

export const projectLabelsRelations = relations(projectLabels, ({ one, many }) => ({
  creator: one(user, {
    fields: [projectLabels.createdBy],
    references: [user.id],
  }),
  assignments: many(projectLabelAssignments),
}))

export const projectLabelAssignmentsRelations = relations(projectLabelAssignments, ({ one }) => ({
  project: one(projectsTable, {
    fields: [projectLabelAssignments.projectId],
    references: [projectsTable.id],
  }),
  label: one(projectLabels, {
    fields: [projectLabelAssignments.labelId],
    references: [projectLabels.id],
  }),
  assignedByUser: one(user, {
    fields: [projectLabelAssignments.assignedBy],
    references: [user.id],
  }),
}))

export const merchandiseGroupConfigurationRelations = relations(
  merchandiseGroupConfiguration,
  ({ one }) => ({
    merchandiseGroup: one(merchandiseGroup, {
      fields: [merchandiseGroupConfiguration.merchandiseGroupId],
      references: [merchandiseGroup.id],
    }),
  }),
)

export const merchandiseGroupRelations = relations(merchandiseGroup, ({ one, many }) => ({
  merchandiseGroupConfigurations: many(merchandiseGroupConfiguration),
  category: one(category, {
    fields: [merchandiseGroup.categoryId],
    references: [category.id],
  }),
}))

export const invitationRelations = relations(invitation, ({ one }) => ({
  organization: one(organization, {
    fields: [invitation.organizationId],
    references: [organization.id],
  }),
  user: one(user, {
    fields: [invitation.inviterId],
    references: [user.id],
  }),
}))

export const memberRelations = relations(member, ({ one }) => ({
  organization: one(organization, {
    fields: [member.organizationId],
    references: [organization.id],
  }),
  user: one(user, {
    fields: [member.userId],
    references: [user.id],
  }),
}))

export const sessionRelations = relations(session, ({ one }) => ({
  user: one(user, {
    fields: [session.userId],
    references: [user.id],
  }),
}))

export const accountRelations = relations(account, ({ one }) => ({
  user: one(user, {
    fields: [account.userId],
    references: [user.id],
  }),
}))

export const categoryRelations = relations(category, ({ many }) => ({
  merchandiseGroups: many(merchandiseGroup),
}))

export const storesRelations = relations(stores, ({ one, many }) => ({
  country: one(countries, {
    fields: [stores.countryId],
    references: [countries.id],
  }),
  projects: many(projectsTable),
  configurations: many(storeConfigurations),
  metadataExtensions: one(storeMetadataExtensions, {
    fields: [stores.id],
    references: [storeMetadataExtensions.storeId],
  }),
}))

export const countriesRelations = relations(countries, ({ many }) => ({
  stores: many(stores),
}))

export const storeConfigurationsRelations = relations(storeConfigurations, ({ one }) => ({
  store: one(stores, {
    fields: [storeConfigurations.storeId],
    references: [stores.id],
  }),
}))

export const storeMetadataExtensionsRelations = relations(storeMetadataExtensions, ({ one }) => ({
  store: one(stores, {
    fields: [storeMetadataExtensions.storeId],
    references: [stores.id],
  }),
}))

export const projectPresenceRelations = relations(projectPresence, ({ one }) => ({
  project: one(projectsTable, {
    fields: [projectPresence.projectId],
    references: [projectsTable.id],
  }),
  user: one(user, {
    fields: [projectPresence.userId],
    references: [user.id],
  }),
}))

// =============================================================================
// Change Project Relations
// =============================================================================

export const changeProjectsRelations = relations(changeProjects, ({ one, many }) => ({
  owner: one(user, {
    fields: [changeProjects.ownerId],
    references: [user.id],
  }),
  closedBy: one(user, {
    fields: [changeProjects.closedById],
    references: [user.id],
  }),
  targets: many(changeProjectTargets),
  requests: many(changeProjectRequests),
  runs: many(changeProjectRuns),
}))

export const changeProjectTargetsRelations = relations(changeProjectTargets, ({ one }) => ({
  changeProject: one(changeProjects, {
    fields: [changeProjectTargets.changeProjectId],
    references: [changeProjects.id],
  }),
  sourceProject: one(projectsTable, {
    fields: [changeProjectTargets.sourceProjectId],
    references: [projectsTable.id],
  }),
}))

export const changeProjectRequestsRelations = relations(changeProjectRequests, ({ one }) => ({
  changeProject: one(changeProjects, {
    fields: [changeProjectRequests.changeProjectId],
    references: [changeProjects.id],
  }),
}))

export const changeProjectRunsRelations = relations(changeProjectRuns, ({ one, many }) => ({
  changeProject: one(changeProjects, {
    fields: [changeProjectRuns.changeProjectId],
    references: [changeProjects.id],
  }),
  requestedBy: one(user, {
    fields: [changeProjectRuns.requestedById],
    references: [user.id],
  }),
  retryOfRun: one(changeProjectRuns, {
    fields: [changeProjectRuns.retryOfRunId],
    references: [changeProjectRuns.id],
  }),
  rootRun: one(changeProjectRuns, {
    fields: [changeProjectRuns.rootRunId],
    references: [changeProjectRuns.id],
  }),
  items: many(changeProjectRunItems),
}))

export const changeProjectRunItemsRelations = relations(changeProjectRunItems, ({ one }) => ({
  run: one(changeProjectRuns, {
    fields: [changeProjectRunItems.changeProjectRunId],
    references: [changeProjectRuns.id],
  }),
  sourceProject: one(projectsTable, {
    fields: [changeProjectRunItems.sourceProjectId],
    references: [projectsTable.id],
  }),
  retryOfItem: one(changeProjectRunItems, {
    fields: [changeProjectRunItems.retryOfItemId],
    references: [changeProjectRunItems.id],
  }),
  rootItem: one(changeProjectRunItems, {
    fields: [changeProjectRunItems.rootItemId],
    references: [changeProjectRunItems.id],
  }),
  publishedProject: one(projectsTable, {
    fields: [changeProjectRunItems.publishedProjectId],
    references: [projectsTable.id],
  }),
}))

// =============================================================================
// Feature Flags Relations
// =============================================================================

export const featureFlagsRelations = relations(featureFlags, ({ one, many }) => ({
  createdBy: one(user, {
    fields: [featureFlags.createdById],
    references: [user.id],
  }),
  flagEnvironments: many(flagEnvironments),
  flagUserOverrides: many(flagUserOverrides),
  flagChangeLogs: many(flagChangeLog),
}))

export const flagEnvironmentsRelations = relations(flagEnvironments, ({ one }) => ({
  flag: one(featureFlags, {
    fields: [flagEnvironments.flagId],
    references: [featureFlags.id],
  }),
}))

export const flagUserOverridesRelations = relations(flagUserOverrides, ({ one }) => ({
  flag: one(featureFlags, {
    fields: [flagUserOverrides.flagId],
    references: [featureFlags.id],
  }),
  user: one(user, {
    fields: [flagUserOverrides.userId],
    references: [user.id],
  }),
}))

export const flagChangeLogRelations = relations(flagChangeLog, ({ one }) => ({
  flag: one(featureFlags, {
    fields: [flagChangeLog.flagId],
    references: [featureFlags.id],
  }),
  changedBy: one(user, {
    fields: [flagChangeLog.changedById],
    references: [user.id],
  }),
}))

// =============================================================================
// Evaluation Relations
// =============================================================================

export const evaluationBenchmarkRelations = relations(evaluationBenchmarks, ({ one, many }) => ({
  project: one(projectsTable, {
    fields: [evaluationBenchmarks.projectId],
    references: [projectsTable.id],
  }),
  store: one(stores, {
    fields: [evaluationBenchmarks.storeId],
    references: [stores.id],
  }),
  configurationVersion: one(configurationVersions, {
    fields: [evaluationBenchmarks.configVersionId],
    references: [configurationVersions.id],
  }),
  runs: many(evaluationRuns),
}))

export const evaluationRunRelations = relations(evaluationRuns, ({ one }) => ({
  batch: one(evaluationBatches, {
    fields: [evaluationRuns.batchId],
    references: [evaluationBatches.id],
  }),
  benchmark: one(evaluationBenchmarks, {
    fields: [evaluationRuns.benchmarkId],
    references: [evaluationBenchmarks.id],
  }),
  configurationVersion: one(configurationVersions, {
    fields: [evaluationRuns.configVersionId],
    references: [configurationVersions.id],
  }),
}))

export const evaluationBatchRelations = relations(evaluationBatches, ({ one, many }) => ({
  configurationVersion: one(configurationVersions, {
    fields: [evaluationBatches.configVersionId],
    references: [configurationVersions.id],
  }),
  runs: many(evaluationRuns),
}))
